import { useTranslation } from "react-i18next";
import { MICROSOFT_SETTINGS_SAVED_EVENT } from "components/layout/MicrosoftSecretExpiryBanner";
import { updateMicrosoftSettings } from "services/company.service";
import { Field, FIELD_INPUT_CLASS } from "../catalog-form-parts";
import { saveErrorKey } from "../save-error";
import {
  ChangeLink,
  EditPanel,
  SettingsCard,
  StatusBanner,
  useInlineEdit,
  useSaveWithToast,
} from "../ui";
import {
  checkVerdict,
  MicrosoftConnectionCheck,
} from "../use-microsoft-connection-check";

/** What the company record holds of the Microsoft app; the secret is only its last 4 characters. */
type MicrosoftApp = {
  clientId?: string;
  tenantId?: string;
  secret?: string;
  /** 'YYYY-MM-DD' */
  secretExpiresAt?: string | null;
};

// The server's codes for a refused save, and the text each one gets. A non-Master
// still gets a 400 code here instead of a 403.
const MICROSOFT_ERROR_KEYS = {
  MS_DATA_REQUIRED: "settings_ms_error_data_required",
  MS_SECRET_EXPIRES_AT_INVALID: "settings_ms_error_expiry_invalid",
  COMPANY_USER_MASTER_NOT_FOUND: "settings_master_only",
};

/** One saved value of the app in the read view. */
function ReadValue({ label, value }: { label: string; value?: string | null }) {
  const { t } = useTranslation();

  return (
    <div>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-gray-900">
        {value || t("settings_none")}
      </dd>
    </div>
  );
}

interface MicrosoftConnectTabProps {
  companyId: string;
  microsoft?: MicrosoftApp;
  /** Loads the company again; a save is done when that has finished. */
  onRefresh: () => Promise<unknown>;
  /** Run after a save went through, to see that the new details work; its result shows in the card. */
  check: MicrosoftConnectionCheck;
}

export default function MicrosoftConnectTab({
  companyId,
  microsoft = {},
  onRefresh,
  check,
}: MicrosoftConnectTabProps) {
  const { t } = useTranslation();
  // The secret is never loaded into the form: an empty field keeps the stored one.
  const edit = useInlineEdit({
    clientId: microsoft.clientId ?? "",
    tenantId: microsoft.tenantId ?? "",
    secret: "",
    secretExpiresAt: microsoft.secretExpiresAt ?? "",
  });
  const { save, saving, error } = useSaveWithToast();

  const setField = (field: keyof typeof edit.draft, value: string) =>
    edit.setDraft({ ...edit.draft, [field]: value });

  const saveApp = async () => {
    const { clientId, tenantId, secret, secretExpiresAt } = edit.draft;
    // A key copied from Azure or Excel often carries a trailing space or newline.
    const typedSecret = secret.trim();
    const saved = await save(
      async () => {
        await updateMicrosoftSettings(
          {
            clientId,
            tenantId,
            secretExpiresAt: secretExpiresAt || null,
            ...(typedSecret ? { secret: typedSecret } : {}),
          },
          companyId
        );
        window.dispatchEvent(new Event(MICROSOFT_SETTINGS_SAVED_EVENT));
        await onRefresh();
      },
      { errorMessage: (err) => t(saveErrorKey(err, MICROSOFT_ERROR_KEYS)) }
    );
    if (saved) {
      edit.cancel();
      check.run();
    }
  };

  let secretText: string | undefined;
  if (microsoft.secret) {
    secretText = microsoft.secretExpiresAt
      ? t("settings_ms_secret_entered_until", {
          date: microsoft.secretExpiresAt,
        })
      : t("settings_secret_entered");
  }

  // While the check after a save runs there is nothing to report yet.
  let checkBanner = null;
  if (check.checking) {
    checkBanner = <StatusBanner tone="info" title={t("storage_loading")} />;
  } else if (check.result) {
    const verdict = checkVerdict(check.result);
    checkBanner = (
      <StatusBanner tone={verdict.tone} title={t(verdict.textKey)} />
    );
  }

  return (
    <SettingsCard
      title={t("settings_storage_onedrive_heading")}
      description={t("settings_ms_card_description")}
      actions={!edit.editing && <ChangeLink onClick={edit.start} />}
    >
      {checkBanner && <div className="mb-3">{checkBanner}</div>}
      {edit.editing ? (
        <EditPanel
          saving={saving}
          error={error}
          saveLabel={t("settings_ms_save_and_check")}
          onSave={saveApp}
          onCancel={edit.cancel}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Field htmlFor="ms-client-id" label={t("settings_ms_client_id")}>
              <input
                id="ms-client-id"
                className={FIELD_INPUT_CLASS}
                value={edit.draft.clientId}
                onChange={(e) => setField("clientId", e.target.value)}
              />
            </Field>
            <Field htmlFor="ms-tenant-id" label={t("settings_ms_tenant_id")}>
              <input
                id="ms-tenant-id"
                className={FIELD_INPUT_CLASS}
                value={edit.draft.tenantId}
                onChange={(e) => setField("tenantId", e.target.value)}
              />
            </Field>
            <Field htmlFor="ms-secret" label={t("settings_ms_secret")}>
              <input
                id="ms-secret"
                type="password"
                autoComplete="new-password"
                className={FIELD_INPUT_CLASS}
                value={edit.draft.secret}
                placeholder={
                  microsoft.secret
                    ? t("microsoft_secret_saved_placeholder", {
                        last4: microsoft.secret,
                      })
                    : undefined
                }
                onChange={(e) => setField("secret", e.target.value)}
              />
            </Field>
            <Field
              htmlFor="ms-secret-expires-at"
              label={t("microsoft_secret_expires_at")}
              hint={t("microsoft_secret_expires_at_hint")}
            >
              <input
                id="ms-secret-expires-at"
                type="date"
                className={FIELD_INPUT_CLASS}
                value={edit.draft.secretExpiresAt}
                onChange={(e) => setField("secretExpiresAt", e.target.value)}
              />
            </Field>
          </div>
        </EditPanel>
      ) : (
        <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
          <ReadValue
            label={t("settings_ms_client_id")}
            value={microsoft.clientId}
          />
          <ReadValue
            label={t("settings_ms_tenant_id")}
            value={microsoft.tenantId}
          />
          <ReadValue label={t("settings_ms_secret")} value={secretText} />
        </dl>
      )}
    </SettingsCard>
  );
}
